import React, { useState, useEffect } from 'react';
import driverService from '../../api/services/driverService';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/bootstrap.css';
import countryList from 'react-select-country-list';
import { toast } from 'react-toastify';
import useMapLocation from '../../hooks/useMapLocation';
import { Autocomplete, GoogleMap } from '@react-google-maps/api';
import { useUser } from '../../hooks/useUser';
import SafeImage from '../../components/common/SafeImage';

export default function DriverProfileSettings() {
    const { data: user, isLoading } = useUser();
    const [driverInfo, setDriverInfo] = useState({
        firstName: '',
        email: '',
        phoneNumber: '',
        vechileType: '',
        vechileNumber: '',
        profile_image: '',
        address_line1: '',
    });
    const [loading, setLoading] = useState(false);
    const [profileImage, setProfileImage] = useState(null);
    const [profileImagePreview, setProfileImagePreview] = useState('');
    const [countries] = useState(countryList().getData());
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [formData, setFormData] = useState({ longitude: '', latitude: '' });
    const [showLocationModal, setShowLocationModal] = useState(false);
    const [isLocationLoaded, setIsLocationLoaded] = useState(false);
    const [locationLoading, setLocationLoading] = useState(false);
    const containerStyle = { width: '100%', height: '300px' };
    const centerDefault = { lat: 20.5937, lng: 78.9629 };
    const [mapCenter, setMapCenter] = useState(centerDefault);
    const mapRef = React.useRef(null);
    const {
      isLoaded: mapIsLoaded,
      country: mapCountry,
      latLng,
      address: mapAddress,
      setAddress,
      setLatLng,
      onPlaceChanged,
      error: mapError,
      autocompleteRef
    } = useMapLocation();

    useEffect(() => {
        if (user) {
            fetchDriverData();
        }
    }, [user]);

    useEffect(() => {
        if (driverInfo.profile_image && typeof driverInfo.profile_image === 'string' && driverInfo.profile_image.trim() !== '') {
            setProfileImagePreview(
                driverInfo.profile_image.startsWith('http')
                    ? driverInfo.profile_image
                    : import.meta.env.VITE_API_URL ? `${import.meta.env.VITE_API_URL}${driverInfo.profile_image}` : `http://localhost:8000${driverInfo.profile_image}`
            );
        }
    }, [driverInfo.profile_image]);

    useEffect(() => {
      if (latLng) {
        setMapCenter(latLng);
      }
    }, [latLng]);

    const fetchDriverData = async () => {
        try {
            setLoading(true);
            const result = await driverService.getDriverById(user.user_id);
            if (result.success) {
                const driver = result.data;
                setDriverInfo({
                    firstName: driver.user?.name || '',
                    email: driver.user?.email || '',
                    phoneNumber: driver.user?.phone_no || '',
                    vechileType: driver.vehicle_type || '',
                    vechileNumber: driver.vehicle_no || '',
                    profile_image: driver.user?.profile_image || '',
                    address_line1: driver.address?.address_line1 || driver.location || '',
                });
                if ((driver.latitude || driver.latitude === 0) && (driver.longitude || driver.longitude === 0)) {
                    setFormData({
                        latitude: driver.latitude.toString(),
                        longitude: driver.longitude.toString()
                    });
                    setMapCenter({
                        lat: parseFloat(driver.latitude),
                        lng: parseFloat(driver.longitude)
                    });
                    setLatLng({
                        lat: parseFloat(driver.latitude),
                        lng: parseFloat(driver.longitude)
                    });
                }
            } else {
                toast.error(result.message || 'Failed to fetch driver data');
            }
        } catch (error) {
            toast.error('Failed to fetch driver data');
        } finally {
            setLoading(false);
        }
    };

    const handleInfoChange = (e) => {
        const { name, value } = e.target;
        setDriverInfo(prevInfo => ({ ...prevInfo, [name]: value }));
    };

    const handlePhoneChange = (value) => {
        setDriverInfo(prev => ({ ...prev, phoneNumber: value }));
    };

    const handleProfileImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setProfileImage(file);
            setProfileImagePreview(URL.createObjectURL(file));
            setDriverInfo(prev => ({ ...prev, profile_image: file }));
        }
    };

    const handleLocationSelect = () => {
      if (mapCenter) {
        setFormData((prev) => ({
          ...prev,
          longitude: mapCenter.lng.toString(),
          latitude: mapCenter.lat.toString()
        }));
        if (window.google && window.google.maps) {
          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode({ location: mapCenter }, (results, status) => {
            if (status === "OK" && results[0]) {
              setAddress(results[0].formatted_address);
              setDriverInfo((prev) => ({
                ...prev,
                address_line1: results[0].formatted_address,
              }));
            }
          });
        }
        setShowLocationModal(false);
        setIsLocationLoaded(true);
        if (errors.location) {
          setErrors(prev => ({ ...prev, location: '' }));
        }
      }
    };
    const handleOpenLocationModal = () => {
      setShowLocationModal(true);
      setTouched(prev => ({ ...prev, location: true }));
      if (!latLng) {
        setLatLng(centerDefault);
      }
    };
    const handleGetCurrentLocation = () => {
      setLocationLoading(true);
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords;
            setLatLng({ lat: latitude, lng: longitude });
            if (window.google && window.google.maps) {
              const geocoder = new window.google.maps.Geocoder();
              geocoder.geocode({ location: { lat: latitude, lng: longitude } }, (results, status) => {
                if (status === "OK" && results[0]) {
                  setAddress(results[0].formatted_address);
                }
                setLocationLoading(false);
              });
            } else {
              setLocationLoading(false);
            }
          },
          (error) => {
            toast.error("Unable to fetch your location. Please allow location access.");
            setLocationLoading(false);
          }
        );
      } else {
        toast.error("Geolocation is not supported by your browser.");
        setLocationLoading(false);
      }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setTouched({
            firstName: true,
            email: true,
            phoneNumber: true,
            vechileType: true,
            vechileNumber: true,
            location: true,
            profile_image: true,
        });
        if (Object.values(validateForm()).length > 0) return;
        try {
            setLoading(true);
            const payload = {
                name: driverInfo.firstName,
                email: driverInfo.email,
                phone_no: driverInfo.phoneNumber,
                vehicle_type: driverInfo.vechileType,
                vehicle_no: driverInfo.vechileNumber,
                longitude: formData.longitude,
                latitude: formData.latitude,
                location: driverInfo.address_line1 || mapAddress || '',
            };
            const result = await driverService.updateDriver(user.user_id, payload);
            if (result.success) {
                if (profileImage) {
                    const imgRes = await driverService.updateProfileImage(user.user_id, profileImage);
                    if (imgRes.success) {
                        toast.success('Profile and image updated successfully');
                        fetchDriverData();
                    } else {
                        toast.error(imgRes.message || 'Failed to update profile image');
                    }
                } else {
                    toast.success(result.message || 'Profile updated successfully');
                    fetchDriverData();
                }
            } else {
                const errorMessage = result.errors ? Object.values(result.errors).flat().join(', ') : result.message || 'Operation failed';
                toast.error(errorMessage);
            }
        } catch (error) {
            toast.error('Failed to save driver: ' + (error.message || 'Unknown error'));
        } finally {
            setLoading(false);
        }
    };

    const validateForm = () => {
        const newErrors = {};
        if (!driverInfo.firstName) newErrors.firstName = 'Name is required';
        if (!driverInfo.email) newErrors.email = 'Email is required';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(driverInfo.email)) newErrors.email = 'Please enter a valid email address';
        if (!driverInfo.phoneNumber) newErrors.phoneNumber = 'Phone number is required';
        else if (!/^\+?[1-9]\d{7,14}$/.test(driverInfo.phoneNumber)) newErrors.phoneNumber = 'Please enter a valid international phone number (e.g., +1234567890)';
        if (!driverInfo.vechileType) newErrors.vechileType = 'Vehicle type is required';
        if (!driverInfo.vechileNumber) newErrors.vechileNumber = 'Vehicle number is required';
        if (!formData.longitude || !formData.latitude) newErrors.location = 'Business location is required';
        return newErrors;
    };

    useEffect(() => { setErrors(validateForm()); }, [driverInfo, formData]);

    if (isLoading || loading) return <div>Loading...</div>;

    return (
        <div className="container-fluid">
            <div className="row">
                <div className="col-12">
                    <div className="row">
                        <div className="col-sm-8 m-auto">
                            <div className="card">
                                <div className="card-body">
                                    <div className="title-header option-title">
                                        <h5>Profile Settings</h5>
                                    </div>
                                    <form className="theme-form theme-form-2 mega-form" onSubmit={handleSubmit}>
                                        <div className="row">
                                            <div className="mb-4 row align-items-center">
                                                <label className="form-label-title col-lg-2 col-md-3 mb-0">Name</label>
                                                <div className="col-md-9 col-lg-10">
                                                    <input className="form-control" type="text" name="firstName" value={driverInfo.firstName} onChange={handleInfoChange} required />
                                                    {touched.firstName && errors.firstName && <div className="text-danger small mt-1">{errors.firstName}</div>}
                                                </div>
                                            </div>
                                            <div className="mb-4 row align-items-center">
                                                <label className="col-lg-2 col-md-3 col-form-label form-label-title">Email Address</label>
                                                <div className="col-md-9 col-lg-10">
                                                    <input className="form-control" type="email" name="email" value={driverInfo.email} onChange={handleInfoChange} required />
                                                    {touched.email && errors.email && <div className="text-danger small mt-1">{errors.email}</div>}
                                                </div>
                                            </div>
                                            <div className="mb-4 row align-items-center">
                                                <label className="col-lg-2 col-md-3 col-form-label form-label-title">Phone Number</label>
                                                <div className="col-md-9 col-lg-10">
                                                    <PhoneInput country={'us'} value={driverInfo.phoneNumber} onChange={handlePhoneChange} inputProps={{ name: 'phoneNumber', required: true, className: 'form-control', onBlur: () => setTouched(prev => ({ ...prev, phoneNumber: true })) }} containerStyle={{ width: '100%' }} inputStyle={{ width: '100%', height: 45, fontSize: '1rem' }} dropdownStyle={{ zIndex: 9999 }} />
                                                    {touched.phoneNumber && errors.phoneNumber && <div className="text-danger small mt-1">{errors.phoneNumber}</div>}
                                                </div>
                                            </div>
                                            <div className="mb-4 row align-items-center">
                                                <label className="col-lg-2 col-md-3 col-form-label form-label-title">Vehicle Type</label>
                                                <div className="col-md-9 col-lg-10">
                                                    <input className="form-control" type="text" name="vechileType" value={driverInfo.vechileType} onChange={handleInfoChange} required />
                                                    {touched.vechileType && errors.vechileType && <div className="text-danger small mt-1">{errors.vechileType}</div>}
                                                </div>
                                            </div>
                                            <div className="mb-4 row align-items-center">
                                                <label className="col-lg-2 col-md-3 col-form-label form-label-title">Vehicle Number</label>
                                                <div className="col-md-9 col-lg-10">
                                                    <input className="form-control" type="text" name="vechileNumber" value={driverInfo.vechileNumber} onChange={handleInfoChange} required />
                                                    {touched.vechileNumber && errors.vechileNumber && <div className="text-danger small mt-1">{errors.vechileNumber}</div>}
                                                </div>
                                            </div>
                                            <div className="mb-4 row align-items-center">
                                                <label className="form-label-title col-lg-2 col-md-3 mb-0">Profile Image</label>
                                                <div className="col-md-9 col-lg-10">
                                                    <input className="form-control" type="file" accept="image/*" onChange={handleProfileImageChange} />
                                                    <SafeImage src={profileImagePreview || '/avatar-placeholder.png'} alt="Profile Preview" style={{ width: 80, height: 80, objectFit: 'cover', marginTop: 8, borderRadius: 8 }} />
                                                </div>
                                            </div>
                                            <div className="mb-4 row align-items-center">
                                                <label className="form-label-title col-lg-2 col-md-3 mb-0">Business Location</label>
                                                <div className="col-md-9 col-lg-10">
                                                    <div className={`form-control text-start${touched.location && errors.location ? ' is-invalid' : ''}`} style={{ backgroundColor: 'white', cursor: loading ? 'not-allowed' : 'pointer', minHeight: '45px', display: 'flex', alignItems: 'center', padding: '0 12px' }} tabIndex={0} onClick={handleOpenLocationModal} role="button" aria-disabled={loading}>
                                                        <i className="fa fa-map-marker-alt text-primary me-2"></i>
                                                        <span className={formData.longitude && formData.latitude ? 'text-dark small' : 'text-muted small'} style={{ fontWeight: 400 }}>
                                                            {formData.longitude && formData.latitude ? 'Location Selected' : 'Click to select your business location'}
                                                        </span>
                                                    </div>
                                                    {touched.location && errors.location && <div className="invalid-feedback d-block">{errors.location}</div>}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="card-submit-button">
                                            <button className="btn btn-animation ms-auto" type="submit" disabled={loading || Object.values(errors).length > 0} style={{ backgroundColor: (loading || Object.values(errors).length > 0) ? '#6c757d' : '', color: (loading || Object.values(errors).length > 0) ? '#ffffff' : '', cursor: (loading || Object.values(errors).length > 0) ? 'not-allowed' : 'pointer', opacity: (loading || Object.values(errors).length > 0) ? 0.6 : 1 }}>
                                                {loading ? 'Saving...' : 'Save Changes'}
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {showLocationModal && mapIsLoaded && (
                <>
                    <div className="modal-backdrop fade show" onClick={() => setShowLocationModal(false)}></div>
                    <div className="modal fade show d-block" tabIndex="-1" role="dialog" aria-labelledby="locationModalLabel" aria-modal="true" style={{ background: 'rgba(0,0,0,0.1)' }}>
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <div>
                                        <h5 className="modal-title" id="locationModalLabel">Choose your Business Location</h5>
                                        <h6 className="mb-0 text-muted">{mapCountry}</h6>
                                        <p className="mt-1 text-muted mb-0" style={{ fontSize: '0.95rem' }}>
                                            Enter your address and we will specify the offer for your area.
                                        </p>
                                    </div>
                                    <button type="button" className="btn-close" aria-label="Close" onClick={() => setShowLocationModal(false)}></button>
                                </div>
                                <div className="modal-body">
                                    <div className="mb-3">
                                        <div className="input-group">
                                            <span className="input-group-text bg-white border-end-0" style={{ paddingRight: 0 }}>
                                                <i className="fa fa-search text-muted"></i>
                                            </span>
                                            {mapIsLoaded && (
                                                <Autocomplete onLoad={ref => (autocompleteRef.current = ref)} onPlaceChanged={onPlaceChanged}>
                                                    <input type="text" placeholder="Search address" className="form-control border-start-0" style={{ maxWidth: 300 }} value={mapAddress} onChange={e => setAddress(e.target.value)} />
                                                </Autocomplete>
                                            )}
                                            <button onClick={handleLocationSelect} className="btn btn-primary ms-2 d-flex align-items-center gap-2" disabled={!mapCenter} type="button">
                                                <i className="fas fa-map-marker-alt"></i>
                                                Set Location
                                            </button>
                                            {locationLoading && (
                                                <div style={{ textAlign: 'center', margin: '1rem 0' }}>
                                                    <div className="spinner-border" role="status" />
                                                    <div>Fetching your current location...</div>
                                                </div>
                                            )}
                                            <button type="button" className="btn ms-2 d-flex align-items-center gap-2" onClick={handleGetCurrentLocation} style={{ minWidth: 140 }} disabled={locationLoading}>
                                                <i className="fa fa-location-arrow me-1" style={{ opacity: 0.7 }}></i>
                                                {locationLoading ? 'Locating...' : 'Get Current Location'}
                                            </button>
                                        </div>
                                    </div>
                                    {mapAddress && (
                                        <div className="mb-2">
                                            <p className="mb-0"><strong>Address:</strong> {mapAddress}</p>
                                        </div>
                                    )}
                                    {mapError && <p className="text-danger small mb-2">{mapError}</p>}
                                    <div className="mb-2">
                                        <div style={{ position: 'relative', width: '100%', height: 300 }}>
                                            <GoogleMap mapContainerStyle={containerStyle} center={mapCenter} zoom={latLng ? 15 : 6} onLoad={map => (mapRef.current = map)} onDragEnd={() => { if (mapRef.current) { const newCenter = mapRef.current.getCenter(); setMapCenter({ lat: newCenter.lat(), lng: newCenter.lng() }); } }} />
                                            <div style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -100%)', pointerEvents: 'none', zIndex: 2 }}>
                                                <i className="fas fa-map-marker-alt" style={{ fontSize: 36, color: '#d00' }} />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={() => setShowLocationModal(false)}>
                                        Cancel
                                    </button>
                                    <button type="button" className="btn btn-primary" onClick={handleLocationSelect} disabled={!mapCenter}>
                                        Save Location
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
} 